import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  createQueuedTextMessage,
  deleteQueuedTextMessage,
  listQueuedTextMessages,
  saveQueuedTextMessage,
} from '../utils/offlineTextOutbox';

const ChatContext = createContext(null);

const toTimestamp = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    const normalized = value < 1e12 ? value * 1000 : value;
    return Number.isNaN(normalized) ? null : normalized;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const getChatActivityTimestampMs = (chat) => {
  if (!chat) {
    return 0;
  }
  const conversationCandidates = [
    chat.last_activity_at,
    chat.last_message_at,
    chat.last_message_timestamp,
    chat.last_incoming_at,
    chat.last_outgoing_at,
  ];
  let maxTimestamp = 0;
  for (const candidate of conversationCandidates) {
    const timestamp = toTimestamp(candidate);
    if (timestamp !== null && timestamp > maxTimestamp) {
      maxTimestamp = timestamp;
    }
  }
  // `updated_at` can represent profile/assignment metadata, not a message.
  return maxTimestamp || toTimestamp(chat.created_at) || 0;
};

const getChatLastMessageAtRaw = (chat) => {
  if (!chat) {
    return null;
  }
  const raw = chat.last_message_at ?? chat.last_message_timestamp ?? null;
  if (raw !== null && raw !== undefined && raw !== '') {
    return raw;
  }
  const computed = getChatActivityTimestampMs(chat);
  return computed ? new Date(computed).toISOString() : null;
};

const getChatLastMessageAtMs = (chat) => getChatActivityTimestampMs(chat);

const normalizeChatMessageTimestamps = (messages) => (Array.isArray(messages) ? messages : [])
  .filter(Boolean)
  .map((msg) => {
    let normalized = null;
    if (msg.timestamp) {
      const time = new Date(msg.timestamp).getTime();
      normalized = Number.isNaN(time) ? null : new Date(time).toISOString();
    } else if (typeof msg.ts === 'number') {
      normalized = new Date(msg.ts * 1000).toISOString();
    }
    return {
      ...msg,
      timestamp: normalized || msg.timestamp || null,
    };
  });

const getChatThreadIdRaw = (chat) =>
  chat?.thread_id ?? chat?.id ?? chat?.threadId ?? null;

const getChatThreadIdSortValue = (chat) => {
  const raw = getChatThreadIdRaw(chat);
  if (raw === null || raw === undefined) {
    return '';
  }
  if (typeof raw === 'number') {
    return raw;
  }
  return String(raw);
};

const isChatIdMatch = (chat, chatId) => {
  if (chatId === null || chatId === undefined) {
    return false;
  }
  const raw = getChatThreadIdRaw(chat);
  if (raw === null || raw === undefined) {
    return false;
  }
  return String(raw) === String(chatId);
};

const compareThreadIdDesc = (a, b) => {
  const aValue = getChatThreadIdSortValue(a);
  const bValue = getChatThreadIdSortValue(b);
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return bValue - aValue;
  }
  return String(bValue).localeCompare(String(aValue));
};

const compareChatsByRecency = (a, b) => {
  const timeDiff = getChatLastMessageAtMs(b) - getChatLastMessageAtMs(a);
  if (timeDiff !== 0) {
    return timeDiff;
  }
  return compareThreadIdDesc(a, b);
};

const sortChatsByRecency = (chatList = []) =>
  [...chatList].sort(compareChatsByRecency);

const buildResponseRange = (chatList = []) => {
  if (!chatList.length) {
    return null;
  }
  let newest = null;
  let oldest = null;
  for (const chat of chatList) {
    const timestamp = getChatLastMessageAtMs(chat);
    if (!timestamp) {
      continue;
    }
    if (newest === null || timestamp > newest) {
      newest = timestamp;
    }
    if (oldest === null || timestamp < oldest) {
      oldest = timestamp;
    }
  }
  if (newest === null || oldest === null) {
    return null;
  }
  return {
    newest: new Date(newest).toISOString(),
    oldest: new Date(oldest).toISOString()
  };
};

const assertSortedChats = (chatList, meta = {}) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  for (let i = 0; i < chatList.length - 1; i += 1) {
    const current = chatList[i];
    const next = chatList[i + 1];
    const currentTime = getChatLastMessageAtMs(current);
    const nextTime = getChatLastMessageAtMs(next);
    if (currentTime < nextTime) {
      console.warn('Chat order violation detected', {
        cursor_sent: meta.cursorSent ?? null,
        response_range: meta.responseRange ?? null,
        offending: [
          {
            id: getChatThreadIdRaw(current),
            last_message_at: getChatLastMessageAtRaw(current),
          },
          {
            id: getChatThreadIdRaw(next),
            last_message_at: getChatLastMessageAtRaw(next),
          }
        ]
      });
      break;
    }
  }
};

const buildCursorFromChat = (chat) => {
  if (!chat) {
    return null;
  }
  const rawTimestamp = getChatLastMessageAtRaw(chat);
  const threadId = getChatThreadIdRaw(chat);
  const hasRawTimestamp =
    rawTimestamp !== null && rawTimestamp !== undefined && rawTimestamp !== '';
  if (!hasRawTimestamp || threadId === null || threadId === undefined) {
    const timestampMs = getChatLastMessageAtMs(chat);
    if (!timestampMs || threadId === null || threadId === undefined) {
      return null;
    }
    return {
      last_message_at: new Date(timestampMs).toISOString(),
      thread_id: threadId
    };
  }
  return {
    last_message_at: rawTimestamp,
    thread_id: threadId
  };
};

const deriveCursorFromChats = (chatList = []) => {
  if (!chatList.length) {
    return null;
  }
  return buildCursorFromChat(chatList[chatList.length - 1]);
};

const resolveNextCursor = (serverCursor, sortedChats) => {
  const derivedCursor = deriveCursorFromChats(sortedChats);
  if (!derivedCursor) {
    return serverCursor ?? null;
  }
  if (!serverCursor) {
    return derivedCursor;
  }
  if (typeof serverCursor !== 'object') {
    return serverCursor;
  }
  if (process.env.NODE_ENV !== 'production') {
    const normalizedServerTimestamp =
      serverCursor.last_message_at ?? serverCursor.last_message_timestamp ?? null;
    const normalizedServerThreadId =
      serverCursor.thread_id ?? serverCursor.threadId ?? serverCursor.id ?? null;
    if (
      normalizedServerTimestamp &&
      normalizedServerThreadId != null &&
      (normalizedServerTimestamp !== derivedCursor.last_message_at ||
        String(normalizedServerThreadId) !== String(derivedCursor.thread_id))
    ) {
      console.warn('Cursor mismatch; using derived cursor from sorted chats', {
        server_cursor: serverCursor,
        derived_cursor: derivedCursor
      });
    }
  }
  return derivedCursor;
};

const buildCursorParams = (cursor) => {
  if (!cursor) {
    return {};
  }
  if (typeof cursor === 'string' || typeof cursor === 'number') {
    return { cursor };
  }
  if (typeof cursor === 'object') {
    const lastMessageAt =
      cursor.last_message_at ?? cursor.last_message_timestamp ?? cursor.lastMessageAt ?? null;
    const threadId = cursor.thread_id ?? cursor.threadId ?? cursor.id ?? null;
    if (
      lastMessageAt !== null &&
      lastMessageAt !== undefined &&
      lastMessageAt !== '' &&
      threadId !== null &&
      threadId !== undefined
    ) {
      return {
        cursor_last_message_at: lastMessageAt,
        cursor_thread_id: threadId
      };
    }
  }
  return { cursor };
};

// A cursor page should be quick even for operators with a very large inbox.
const CHAT_PAGE_LIMIT = 50;

// WebSocket payloads originate with Meta and can vary between message types.
// Never allow a partial event to corrupt the inbox state or crash its renderer.
const asMessageArray = (messages) => (Array.isArray(messages) ? messages : []);

const normalizeLiveMessage = (message) => {
  if (!message || typeof message !== 'object' || Array.isArray(message)) {
    return null;
  }
  const content = message.content === null || message.content === undefined
    ? ''
    : typeof message.content === 'string'
      ? message.content
      : String(message.content);
  return {
    ...message,
    content,
    attachments: asMessageArray(message.attachments),
    reactions: asMessageArray(message.reactions),
    metadata: message.metadata && typeof message.metadata === 'object'
      ? message.metadata
      : {},
  };
};

const normalizeChatResponse = (data) => {
  if (Array.isArray(data)) {
    return { items: data, next_cursor: null, has_more: false };
  }
  if (data && Array.isArray(data.items)) {
    return {
      items: data.items,
      next_cursor: data.next_cursor ?? data.nextCursor ?? null,
      has_more: Boolean(data.has_more ?? data.hasMore)
    };
  }
  throw new Error('Invalid response format from server');
};

export const ChatProvider = ({ children, userRole, userId }) => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [activePlatform, setActivePlatform] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [outboxMessages, setOutboxMessages] = useState([]);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const activePlatformRef = useRef('all');
  // A chat fetch may finish after the user has already chosen another row.
  // Keep the most recent selection authoritative so a slow old request cannot
  // put its conversation back on screen.
  const selectionRequestIdRef = useRef(0);
  const loadChatsRef = useRef(null);
  const lastFiltersRef = useRef({});
  const lastFetchMetaRef = useRef({ cursorSent: null, responseRange: null });
  const chatListRequestRef = useRef({ id: 0, controller: null });
  const profileRefreshRef = useRef({
    inFlight: new Set(),
    lastFetchById: new Map(),
  });
  const outboxInFlightRef = useRef(new Set());

  const updateChatMessages = useCallback((chatId, newMessage, options = {}) => {
    const normalizedMessage = normalizeLiveMessage(newMessage);
    if (!normalizedMessage || !chatId) {
      console.warn('Ignoring an invalid live chat message update', { chatId, newMessage });
      return;
    }
    const { chatExists = true } = options;
    const sender = (normalizedMessage.sender || '').toString().toLowerCase();
    const isAgentMessage = ['agent', 'instagram_page', 'facebook_page', 'page', 'business', 'whatsapp_business'].includes(sender);

    setChats(currentChats => {
      let didChange = false;
      const updatedChats = chatExists
        ? currentChats.map(chat => {
            if (!isChatIdMatch(chat, chatId)) {
              return chat;
            }

            const existingMessages = asMessageArray(chat.messages);
            const alreadyExists = existingMessages.some(message => message.id === normalizedMessage.id);
            if (alreadyExists) {
              return chat;
            }
            didChange = true;
            const nextMessages = [...existingMessages, normalizedMessage];

            let unreadCount = chat.unread_count || 0;
            if (isAgentMessage) {
              // An outbound message resolves the customer item for everyone,
              // irrespective of whether it was sent here or in Meta Inbox.
              unreadCount = 0;
            } else if (!alreadyExists) {
              // Viewing a chat never clears its shared awaiting-reply badge.
              unreadCount += 1;
            }

            const lastMessageAt =
              normalizedMessage.timestamp || chat.last_message_at || chat.last_message_timestamp;

            return {
              ...chat,
              messages: nextMessages,
              last_message: normalizedMessage.content,
              last_message_at: lastMessageAt,
              last_message_timestamp: lastMessageAt,
              last_incoming_at: isAgentMessage
                ? chat.last_incoming_at
                : (normalizedMessage.timestamp || chat.last_incoming_at),
              last_outgoing_at: isAgentMessage
                ? (normalizedMessage.timestamp || chat.last_outgoing_at)
                : chat.last_outgoing_at,
              unread_count: unreadCount,
              status: isAgentMessage ? 'assigned' : chat.status
            };
          })
        : currentChats;
      if (!didChange) {
        return currentChats;
      }
      const sorted = sortChatsByRecency(updatedChats);
      assertSortedChats(sorted, lastFetchMetaRef.current);
      return sorted;
    });

    setSelectedChat(currentChat => {
      if (!isChatIdMatch(currentChat, chatId)) {
        return currentChat;
      }

      const existingMessages = asMessageArray(currentChat.messages);
      const alreadyExists = existingMessages.some(message => message.id === normalizedMessage.id);
      if (alreadyExists) {
        return currentChat;
      }
      const nextMessages = [...existingMessages, normalizedMessage];

      return {
        ...currentChat,
        messages: nextMessages,
        last_message: normalizedMessage.content,
        last_message_at:
          normalizedMessage.timestamp || currentChat.last_message_at || currentChat.last_message_timestamp,
        last_message_timestamp:
          normalizedMessage.timestamp || currentChat.last_message_timestamp || currentChat.last_message_at,
        last_incoming_at: isAgentMessage
          ? currentChat.last_incoming_at
          : (normalizedMessage.timestamp || currentChat.last_incoming_at),
        last_outgoing_at: isAgentMessage
          ? (normalizedMessage.timestamp || currentChat.last_outgoing_at)
          : currentChat.last_outgoing_at,
        unread_count: isAgentMessage
          ? 0
          : (alreadyExists ? (currentChat.unread_count || 0) : (currentChat.unread_count || 0) + 1),
        status: isAgentMessage ? 'assigned' : currentChat.status
      };
    });
  }, []);

  const updateMessageReactions = useCallback((chatId, messageId, reactions) => {
    const applyReaction = (chat) => {
      if (!isChatIdMatch(chat, chatId)) return chat;
      return {
        ...chat,
        messages: asMessageArray(chat.messages).map((message) => (
          String(message.id) === String(messageId)
            ? { ...message, reactions: reactions || [] }
            : message
        )),
      };
    };
    setChats((currentChats) => currentChats.map(applyReaction));
    setSelectedChat((currentChat) => (currentChat ? applyReaction(currentChat) : currentChat));
  }, []);

  const loadChats = useCallback(async (platform = 'all', filters = {}) => {
    const previousRequest = chatListRequestRef.current;
    previousRequest.controller?.abort();
    const controller = new AbortController();
    const requestId = previousRequest.id + 1;
    chatListRequestRef.current = { id: requestId, controller };
    setLoading(true);
    setIsLoadingMore(false);
    setError(null);
    activePlatformRef.current = platform;
    lastFiltersRef.current = filters || {};
    setHasMore(false);
    setNextCursor(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const headers = { Authorization: `Bearer ${token}` };
      const chatParams = {};
      if (platform !== 'all') {
        chatParams.platform = platform;
      }
      if (filters) {
        if (filters.unseen !== undefined) {
          chatParams.unseen = filters.unseen;
        }
        if (filters.not_replied !== undefined) {
          chatParams.not_replied = filters.not_replied;
        }
        if (filters.ad_lead !== undefined) {
          chatParams.ad_lead = filters.ad_lead;
        }
        if (filters.assigned_to) {
          chatParams.assigned_to = filters.assigned_to;
        }
        if (filters.inbox_account_id) {
          chatParams.inbox_account_id = filters.inbox_account_id;
        }
        if (filters.q) {
          chatParams.q = filters.q;
        }
      }
      
      const response = await axios.get(`${API}/chats`, { 
        headers, 
        params: {
          ...chatParams,
          limit: CHAT_PAGE_LIMIT
        },
        signal: controller.signal,
        timeout: 30000
      });

      if (chatListRequestRef.current.id !== requestId) {
        return [];
      }

      const { items, next_cursor, has_more } = normalizeChatResponse(response.data);
      const chatsData = items || [];
      const sortedChats = sortChatsByRecency(chatsData);
      lastFetchMetaRef.current = {
        cursorSent: null,
        responseRange: buildResponseRange(chatsData)
      };
      assertSortedChats(sortedChats, lastFetchMetaRef.current);
      setChats(sortedChats);
      setActivePlatform(platform);
      activePlatformRef.current = platform; // Update ref
      setHasMore(has_more);
      setNextCursor(resolveNextCursor(next_cursor, sortedChats));
      setSelectedChat((currentChat) => {
        if (!currentChat) {
          return null;
        }
        const refreshed = chatsData.find(chat =>
          isChatIdMatch(chat, getChatThreadIdRaw(currentChat))
        );
        if (!refreshed) {
          return currentChat;
        }
        return {
          ...currentChat,
          ...refreshed,
          messages: asMessageArray(currentChat.messages),
        };
      });
      return chatsData;
    } catch (error) {
      if (controller.signal.aborted || error?.code === 'ERR_CANCELED') {
        return [];
      }
      console.error('Error loading chats:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load chats';
      if (chatListRequestRef.current.id === requestId) {
        setError(errorMessage);
      }
      throw new Error(errorMessage);
    } finally {
      if (chatListRequestRef.current.id === requestId) {
        setLoading(false);
      }
    }
  }, []);

  const loadMoreChats = useCallback(async () => {
    if (!hasMore || isLoadingMore || !nextCursor) {
      return [];
    }
    setIsLoadingMore(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const headers = { Authorization: `Bearer ${token}` };
      const chatParams = {};
      if (activePlatformRef.current !== 'all') {
        chatParams.platform = activePlatformRef.current;
      }
      const filters = lastFiltersRef.current || {};
      if (filters.unseen !== undefined) {
        chatParams.unseen = filters.unseen;
      }
      if (filters.not_replied !== undefined) {
        chatParams.not_replied = filters.not_replied;
      }
      if (filters.ad_lead !== undefined) {
        chatParams.ad_lead = filters.ad_lead;
      }
      if (filters.assigned_to) {
        chatParams.assigned_to = filters.assigned_to;
      }
      if (filters.inbox_account_id) {
        chatParams.inbox_account_id = filters.inbox_account_id;
      }
      if (filters.q) {
        chatParams.q = filters.q;
      }

      const response = await axios.get(`${API}/chats`, {
        headers,
        params: {
          ...chatParams,
          limit: CHAT_PAGE_LIMIT,
          ...buildCursorParams(nextCursor)
        },
        timeout: 30000
      });

      const { items, next_cursor, has_more } = normalizeChatResponse(response.data);
      const newChats = items || [];
      lastFetchMetaRef.current = {
        cursorSent: nextCursor,
        responseRange: buildResponseRange(newChats)
      };

      let mergedSnapshot = [];
      setChats(currentChats => {
        const existingIds = new Set(
          currentChats
            .map(chat => getChatThreadIdRaw(chat))
            .filter((id) => id !== null && id !== undefined)
            .map((id) => String(id))
        );
        const merged = [
          ...currentChats,
          ...newChats.filter(chat => {
            const id = getChatThreadIdRaw(chat);
            if (id === null || id === undefined) {
              return true;
            }
            return !existingIds.has(String(id));
          })
        ];
        mergedSnapshot = sortChatsByRecency(merged);
        assertSortedChats(mergedSnapshot, lastFetchMetaRef.current);
        return mergedSnapshot;
      });
      setHasMore(has_more);
      setNextCursor(resolveNextCursor(next_cursor, mergedSnapshot));
      return newChats;
    } catch (error) {
      console.error('Error loading more chats:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load more chats';
      setError(errorMessage);
      return [];
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, nextCursor]);

  // Store loadChats in ref for use in handleWebSocketMessage
  loadChatsRef.current = loadChats;

  const selectChat = useCallback(async (chatId) => {
    const requestId = selectionRequestIdRef.current + 1;
    selectionRequestIdRef.current = requestId;
    // Do not leave the previously opened conversation on screen while the
    // next one is loading. It is both misleading and can look like a failed
    // chat selection on slower connections.
    setSelectedChat((current) =>
      String(current?.id) === String(chatId) ? current : null
    );
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const headers = { Authorization: `Bearer ${token}` };
      const [chatResponse, messagesResponse, assignmentHistoryResponse] = await Promise.all([
        axios.get(`${API}/chats/${chatId}`, { headers }),
        axios.get(`${API}/chats/${chatId}/messages`, { headers, params: { limit: 50 } }),
        axios.get(`${API}/chats/${chatId}/assignment-history`, { headers, params: { limit: 50 } }),
      ]);

      // The summary is intentionally light; the transcript and audit trail
      // arrive in independent, bounded pages so a long conversation cannot
      // delay opening its header.
      const chatData = {
        ...(chatResponse.data || {}),
        messages: normalizeChatMessageTimestamps(messagesResponse.data?.items),
        messages_next_cursor: messagesResponse.data?.next_cursor || null,
        messages_has_more: Boolean(messagesResponse.data?.has_more),
        assignment_history: assignmentHistoryResponse.data?.items || [],
        assignment_history_next_cursor: assignmentHistoryResponse.data?.next_cursor || null,
        assignment_history_has_more: Boolean(assignmentHistoryResponse.data?.has_more),
      };

      // Only the server-authorized assigned agent can clear the shared queue
      // unread count. Admin inspection intentionally returns marked: false.
      try {
        const markReadResponse = await axios.post(`${API}/chats/${chatId}/mark_read`, {}, { headers });
        if (markReadResponse.data?.marked) {
          chatData.unread_count = 0;
        }
      } catch (markReadError) {
        console.warn('Failed to mark chat as read:', markReadError);
      }

      // A newer row was selected while this request (or its mark-read call)
      // was in flight. Do not replace the newer conversation with stale data.
      if (requestId !== selectionRequestIdRef.current) {
        return null;
      }
      
      // Merge refreshed chat data back into the list to update fields like profile image.
      setChats((currentChats) =>
        currentChats.map((chat) =>
          isChatIdMatch(chat, chatId)
            ? {
                ...chat,
                profile_pic_url: chatData.profile_pic_url ?? chat.profile_pic_url,
                profile_pic: chatData.profile_pic ?? chat.profile_pic,
                instagram_user: chatData.instagram_user ?? chat.instagram_user,
                facebook_user: chatData.facebook_user ?? chat.facebook_user,
                instagram_user_id: chatData.instagram_user_id ?? chat.instagram_user_id,
                username: chatData.username ?? chat.username,
                unread_count: chatData.unread_count ?? chat.unread_count,
              }
            : chat
        )
      );
      setSelectedChat(chatData);
      return chatData;
    } catch (error) {
      console.error('Error selecting chat:', error);
      if (requestId === selectionRequestIdRef.current) {
        setError(error.response?.data?.detail || error.message || 'Failed to load chat');
      }
      throw error;
    }
  }, []);

  const loadOlderMessages = useCallback(async () => {
    const chatId = selectedChat?.id;
    const cursor = selectedChat?.messages_next_cursor;
    if (!chatId || !cursor || !selectedChat?.messages_has_more || isLoadingOlderMessages) {
      return [];
    }

    setIsLoadingOlderMessages(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await axios.get(`${API}/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { cursor, limit: 50 },
      });
      const pageMessages = normalizeChatMessageTimestamps(response.data?.items);
      setSelectedChat((currentChat) => {
        if (!isChatIdMatch(currentChat, chatId)) return currentChat;
        const knownIds = new Set(asMessageArray(currentChat.messages).map((message) => String(message.id)));
        const olderMessages = pageMessages.filter((message) => !knownIds.has(String(message.id)));
        return {
          ...currentChat,
          messages: [...olderMessages, ...asMessageArray(currentChat.messages)],
          messages_next_cursor: response.data?.next_cursor || null,
          messages_has_more: Boolean(response.data?.has_more),
        };
      });
      return pageMessages;
    } catch (error) {
      console.error('Error loading older messages:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load older messages';
      setError(errorMessage);
      return [];
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }, [isLoadingOlderMessages, selectedChat?.id, selectedChat?.messages_has_more, selectedChat?.messages_next_cursor]);

  const refreshChatProfile = useCallback(async (chatId, options = {}) => {
    const resolvedId = chatId?.toString();
    if (!resolvedId) {
      return null;
    }
    const { force = false } = options;
    const { inFlight, lastFetchById } = profileRefreshRef.current;
    const lastFetch = lastFetchById.get(resolvedId) || 0;
    const now = Date.now();
    const cooldownMs = 5 * 60 * 1000; // avoid hammering profile refreshes
    if (!force && now - lastFetch < cooldownMs) {
      return null;
    }
    if (inFlight.has(resolvedId)) {
      return null;
    }

    const chatSnapshot = chats.find((chat) => String(chat.id) === resolvedId);
    const facebookUserId =
      chatSnapshot?.facebook_user_id ||
      chatSnapshot?.facebook_user?.id ||
      chatSnapshot?.facebook_user?.user_id;
    if (!facebookUserId) {
      return null;
    }

    inFlight.add(resolvedId);
    lastFetchById.set(resolvedId, now);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/facebook/profile-pic/refresh`,
        {
          facebook_user_id: facebookUserId,
          page_id: chatSnapshot?.facebook_page_id || null
        },
        { headers }
      );
      const payload = response.data || {};
      if (!payload.success || !payload.profile_pic_url) {
        return null;
      }

      setChats((currentChats) =>
        currentChats.map((chat) =>
          chat.id === resolvedId || String(chat.id) === resolvedId
            ? {
                ...chat,
                profile_pic_url: payload.profile_pic_url,
              }
            : chat
        )
      );
      setSelectedChat((currentChat) => {
        if (!currentChat || String(currentChat.id) !== resolvedId) {
          return currentChat;
        }
        return {
          ...currentChat,
          profile_pic_url: payload.profile_pic_url,
        };
      });
      return payload;
    } catch (error) {
      const status = error.response?.status;
      if (status === 400) {
        console.warn(`No valid page token found for chat ${resolvedId}`);
      } else if (status === 502) {
        console.warn(`Graph API fetch failed for chat ${resolvedId}`);
      } else if (status === 500) {
        console.warn(`Database save failed for chat ${resolvedId}`);
      } else {
        console.warn('Error refreshing chat profile:', error.message);
      }
      return null;
    } finally {
      inFlight.delete(resolvedId);
    }
  }, [chats]);

  const refreshMultipleChatProfiles = useCallback((chatIds = [], options = {}) => {
    if (!Array.isArray(chatIds) || chatIds.length === 0) {
      return;
    }

    const { force = true } = options; // Default to force refresh for batch operations

    // Fire all refreshes asynchronously with staggered delays to avoid API overload
    chatIds.forEach((chatId, index) => {
      // Stagger requests by 50ms increments to spread API load
      const delay = index * 50;
      setTimeout(() => {
        refreshChatProfile(chatId, { force });
      }, delay);
    });
  }, [refreshChatProfile]);

  const refreshOutbox = useCallback(async () => {
    if (!userId) {
      setOutboxMessages([]);
      return [];
    }
    try {
      const messages = await listQueuedTextMessages(userId);
      setOutboxMessages(messages);
      return messages;
    } catch (error) {
      console.error('Unable to read the offline message outbox:', error);
      return [];
    }
  }, [userId]);

  const postTextMessage = useCallback(async (entry) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    const headers = { Authorization: `Bearer ${token}` };
    const payload = {
      content: entry.content,
      client_message_id: entry.id,
    };
    if (entry.options?.replyTo?.id) {
      payload.reply_to_message_id = entry.options.replyTo.id;
      if (entry.options.replyTo.preview) payload.reply_preview = entry.options.replyTo.preview;
    }
    if (entry.options?.last_message_timestamp) {
      payload.last_message_timestamp = entry.options.last_message_timestamp;
    }
    const response = await axios.post(`${API}/chats/${entry.chat_id}/message`, payload, { headers });
    updateChatMessages(entry.chat_id, response.data);
    try {
      await axios.post(`${API}/chats/${entry.chat_id}/mark_read`, {}, { headers });
    } catch (markReadError) {
      console.warn('Failed to mark messages as read after sending:', markReadError);
    }
    return response.data;
  }, [updateChatMessages]);

  const deliverOutboxMessage = useCallback(async (entry, throwTerminalError = false) => {
    if (outboxInFlightRef.current.has(entry.id)) return null;
    outboxInFlightRef.current.add(entry.id);
    const sendingEntry = {
      ...entry,
      status: 'sending',
      attempts: (entry.attempts || 0) + 1,
      updated_at: new Date().toISOString(),
    };
    try {
      await saveQueuedTextMessage(sendingEntry);
      await refreshOutbox();
      const message = await postTextMessage(sendingEntry);
      await deleteQueuedTextMessage(entry.id);
      await refreshOutbox();
      return message;
    } catch (error) {
      const statusCode = error.response?.status;
      const retryable = !error.response || statusCode === 401 || statusCode === 408 || statusCode === 429 || statusCode >= 500;
      const failedEntry = {
        ...sendingEntry,
        status: retryable ? 'queued' : 'failed',
        last_error: error.response?.data?.detail || error.message || 'Unable to send message',
        updated_at: new Date().toISOString(),
      };
      await saveQueuedTextMessage(failedEntry);
      await refreshOutbox();
      if (!retryable && throwTerminalError) throw error;
      return failedEntry;
    } finally {
      outboxInFlightRef.current.delete(entry.id);
    }
  }, [postTextMessage, refreshOutbox]);

  const processOutbox = useCallback(async () => {
    if (!userId || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    const drain = async () => {
      const messages = await listQueuedTextMessages(userId);
      for (const entry of messages) {
        if (entry.status === 'failed') continue;
        const updatedAt = new Date(entry.updated_at || entry.created_at).getTime();
        if (entry.status === 'sending' && Date.now() - updatedAt < 60000) continue;
        await deliverOutboxMessage(entry, false);
      }
    };
    if (navigator.locks?.request) {
      await navigator.locks.request(
        `ticklegram-text-outbox:${userId}`,
        { ifAvailable: true },
        async (lock) => { if (lock) await drain(); }
      );
      return;
    }
    await drain();
  }, [deliverOutboxMessage, userId]);

  useEffect(() => {
    refreshOutbox().then(() => processOutbox());
    const handleOnline = () => processOutbox();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') processOutbox();
    };
    const retryTimer = window.setInterval(() => processOutbox(), 15000);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(retryTimer);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [processOutbox, refreshOutbox]);

  const sendMessage = useCallback(async (chatId, content, options = {}) => {
    const isQueuedText = content?.trim() && !options.attachments?.length && !options.humanAgent && !options.use_message_tag;
    if (isQueuedText && userId) {
      const entry = createQueuedTextMessage({
        userId,
        chatId,
        platform: options.platform || 'UNKNOWN',
        content: content.trim(),
        options: {
          replyTo: options.replyTo || null,
          last_message_timestamp: options.last_message_timestamp || null,
        },
      });
      await saveQueuedTextMessage(entry);
      await refreshOutbox();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return entry;
      return deliverOutboxMessage(entry, true);
    }

    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { content, attachments: options.attachments || [] };
    const response = await axios.post(`${API}/chats/${chatId}/message`, payload, { headers });
    updateChatMessages(chatId, response.data);
    return response.data;
  }, [deliverOutboxMessage, refreshOutbox, updateChatMessages, userId]);

  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'chat_profile_updated' && data.chat?.chat_id) {
      const updated = data.chat;
      const applyProfile = (chat) => {
        if (!isChatIdMatch(chat, updated.chat_id)) return chat;
        return {
          ...chat,
          username: updated.username || chat.username,
          profile_pic_url: updated.profile_pic_url || chat.profile_pic_url,
          instagram_user: updated.instagram_user
            ? { ...(chat.instagram_user || {}), ...updated.instagram_user }
            : chat.instagram_user,
        };
      };
      setChats((currentChats) => currentChats.map(applyProfile));
      setSelectedChat((currentChat) => currentChat ? applyProfile(currentChat) : currentChat);
      return;
    }

    if (data.type === 'chat_assignment_updated' && data.chat?.chat_id) {
      const updated = data.chat;
      const assignmentEvent = data.assignment_event && typeof data.assignment_event === 'object'
        ? data.assignment_event
        : null;
      const hasAssignedTo = Object.prototype.hasOwnProperty.call(updated, 'assigned_to');
      const hasAssignedAgent = Object.prototype.hasOwnProperty.call(updated, 'assigned_agent');
      const hasStatus = Object.prototype.hasOwnProperty.call(updated, 'status');
      const applyAssignment = (chat) => {
        if (!isChatIdMatch(chat, updated.chat_id)) return chat;
        const existingHistory = Array.isArray(chat.assignment_history) ? chat.assignment_history : [];
        const assignmentHistory = assignmentEvent && !existingHistory.some(
          (event) => String(event?.id) === String(assignmentEvent.id)
        )
          ? [...existingHistory, assignmentEvent]
          : existingHistory;
        return {
          ...chat,
          ...(hasAssignedTo ? { assigned_to: updated.assigned_to } : {}),
          ...(hasAssignedAgent ? { assigned_agent: updated.assigned_agent } : {}),
          ...(hasStatus ? { status: updated.status } : {}),
          ...(assignmentEvent ? { assignment_history: assignmentHistory } : {}),
        };
      };
      setChats((currentChats) => currentChats.map(applyAssignment));
      setSelectedChat((currentChat) => (currentChat ? applyAssignment(currentChat) : currentChat));
      return;
    }

    if (data.type === 'message_reaction') {
      const applyReaction = (chat) => {
        if (!isChatIdMatch(chat, data.chat_id)) return chat;
        return {
          ...chat,
          messages: asMessageArray(chat.messages).map((message) => (
            String(message.id) === String(data.message_id)
              ? { ...message, reactions: data.reactions || [] }
              : message
          )),
        };
      };
      setChats((currentChats) => currentChats.map(applyReaction));
      setSelectedChat((currentChat) => (currentChat ? applyReaction(currentChat) : currentChat));
      return;
    }

    if (data.type === 'message_status' && data.chat_id && data.message_id) {
      const applyStatus = (chat) => {
        if (!isChatIdMatch(chat, data.chat_id)) return chat;
        return {
          ...chat,
          messages: asMessageArray(chat.messages).map((message) => (
            String(message.id) === String(data.message_id)
              ? { ...message, metadata: { ...(message.metadata || {}), ...(data.metadata || {}) } }
              : message
          )),
        };
      };
      setChats((currentChats) => currentChats.map(applyStatus));
      setSelectedChat((currentChat) => (currentChat ? applyStatus(currentChat) : currentChat));
      return;
    }

    if (data.type === 'message_updated' && data.chat_id && data.message) {
      const updatedMessage = normalizeLiveMessage(data.message);
      if (!updatedMessage?.id) {
        console.warn('Ignoring malformed message_updated WebSocket event', data);
        return;
      }
      const applyMessageUpdate = (chat) => {
        if (!isChatIdMatch(chat, data.chat_id)) return chat;
        const existingMessages = asMessageArray(chat.messages);
        const previous = existingMessages.find((message) => String(message.id) === String(updatedMessage.id));
        if (!previous) return chat;
        const messages = existingMessages.map((message) => (
          String(message.id) === String(updatedMessage.id)
            ? { ...message, ...updatedMessage }
            : message
        ));
        return {
          ...chat,
          messages,
          last_message: chat.last_message === previous.content
            ? updatedMessage.content
            : chat.last_message,
        };
      };
      setChats((currentChats) => currentChats.map(applyMessageUpdate));
      setSelectedChat((currentChat) => (currentChat ? applyMessageUpdate(currentChat) : currentChat));
      return;
    }

    if (data.type === 'new_message') {
      const liveMessage = normalizeLiveMessage(data.message);
      if (!data.chat_id || !liveMessage) {
        console.warn('Ignoring malformed new_message WebSocket event', data);
        return;
      }
      const sender = (liveMessage.sender || '').toString().toLowerCase();
      const isOutgoingMessage = ['agent', 'instagram_page', 'facebook_page', 'page', 'business', 'whatsapp_business'].includes(sender);
      // Use functional updates to avoid dependencies on chats
      setChats(currentChats => {
        const chatExists = currentChats.some(chat => isChatIdMatch(chat, data.chat_id));
        
        if (!chatExists) {
          // If chat doesn't exist, we need to reload
          // Use setTimeout to avoid state update during render
          setTimeout(() => {
            if (loadChatsRef.current) {
              loadChatsRef.current(activePlatformRef.current, lastFiltersRef.current);
            }
          }, 0);
          return currentChats;
        }
        
        // Chat exists, update it
        let didChange = false;
        const updatedChats = currentChats.map(chat => {
            if (!isChatIdMatch(chat, data.chat_id)) {
              return chat;
            }

            const existingMessages = asMessageArray(chat.messages);
            const alreadyExists = existingMessages.some(message => message.id === liveMessage.id);
            if (alreadyExists) {
              return chat;
            }
            didChange = true;
            const nextMessages = [...existingMessages, liveMessage];

            let unreadCount = chat.unread_count || 0;
            if (isOutgoingMessage) {
              unreadCount = 0;
            } else {
              // This is a shared awaiting-reply count. Viewing a conversation
              // never clears it; only a successful company reply does.
              if (!alreadyExists) {
                unreadCount += 1;
              }
            }

            return {
              ...chat,
              messages: nextMessages,
              last_message: liveMessage.content,
              last_message_at:
                liveMessage.timestamp || chat.last_message_at || chat.last_message_timestamp,
              last_message_timestamp:
                liveMessage.timestamp || chat.last_message_timestamp || chat.last_message_at,
              last_incoming_at: isOutgoingMessage
                ? chat.last_incoming_at
                : (liveMessage.timestamp || chat.last_incoming_at),
              last_outgoing_at: isOutgoingMessage
                ? (liveMessage.timestamp || chat.last_outgoing_at)
                : chat.last_outgoing_at,
              unread_count: unreadCount,
              status: isOutgoingMessage ? 'assigned' : chat.status
            };
          });
        if (!didChange) {
          return currentChats;
        }
        const sorted = sortChatsByRecency(updatedChats);
        assertSortedChats(sorted, lastFetchMetaRef.current);
        return sorted;
      });
      
      // Update selected chat if it's the one receiving the message
      setSelectedChat(currentChat => {
        if (!isChatIdMatch(currentChat, data.chat_id)) {
          return currentChat;
        }

        const existingMessages = asMessageArray(currentChat.messages);
        const alreadyExists = existingMessages.some(message => message.id === liveMessage.id);
        if (alreadyExists) {
          return currentChat;
        }
        const nextMessages = [...existingMessages, liveMessage];
        const selectedMessageIsOutgoing = ['agent', 'instagram_page', 'facebook_page', 'page', 'business', 'whatsapp_business']
          .includes((liveMessage.sender || '').toString().toLowerCase());

        return {
          ...currentChat,
          messages: nextMessages,
          last_message: liveMessage.content,
          last_message_at:
            liveMessage.timestamp ||
            currentChat.last_message_at ||
            currentChat.last_message_timestamp,
          last_message_timestamp:
            liveMessage.timestamp ||
            currentChat.last_message_timestamp ||
            currentChat.last_message_at,
          last_incoming_at: selectedMessageIsOutgoing
            ? currentChat.last_incoming_at
            : (liveMessage.timestamp || currentChat.last_incoming_at),
          last_outgoing_at: selectedMessageIsOutgoing
            ? (liveMessage.timestamp || currentChat.last_outgoing_at)
            : currentChat.last_outgoing_at,
          unread_count: selectedMessageIsOutgoing
            ? 0
            : (currentChat.unread_count || 0) + 1,
          status: ['agent', 'instagram_page', 'facebook_page', 'page', 'business', 'whatsapp_business'].includes(
            (liveMessage.sender || '').toString().toLowerCase()
          )
            ? 'assigned'
            : currentChat.status
        };
      });

    }
  // This handler must stay referentially stable. InboxPage processes the latest
  // WebSocket event in an effect that depends on this callback. Depending on
  // selectedChat here made each processed message recreate the callback, which
  // replayed the same event and caused React's maximum-update-depth crash.
  }, []);

  const value = {
    chats,
    selectedChat,
    loading,
    isLoadingMore,
    error,
    activePlatform,
    loadChats,
    loadMoreChats,
    hasMore,
    nextCursor,
    selectChat,
    loadOlderMessages,
    isLoadingOlderMessages,
    refreshChatProfile,
    refreshMultipleChatProfiles,
    sendMessage,
    updateChatMessages,
    updateMessageReactions,
    handleWebSocketMessage,
    outboxMessages,
    processOutbox,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
